import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Session-scoped token store. Used as an `Authorization: Bearer` fallback
// because iOS WebKit (Safari + Chrome on iOS) silently drops the
// `withCredentials` cookie on multipart/form-data uploads — breaking every
// photo upload with a 401 "Not authenticated". Kept in sessionStorage so it
// clears on tab close (more conservative than localStorage).
const TOKEN_KEY = "lasovita_access_token";
export function setAccessToken(token) {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch { /* private mode, ignore */ }
}
export function getAccessToken() {
  try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
}

// Build an authenticated URL for a media file. Used in `<img src>` tags where
// axios (and Bearer headers) don't apply. Appends the session token as a
// query parameter so iOS WebKit can still authenticate.
export function mediaUrl(fileId) {
  const t = getAccessToken();
  const base = `${API_BASE}/media/${fileId}/download`;
  return t ? `${base}?token=${encodeURIComponent(t)}` : base;
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Always attach Bearer header if we have a token in sessionStorage.
api.interceptors.request.use((config) => {
  const t = getAccessToken();
  if (t) {
    config.headers = config.headers || {};
    // Never overwrite an explicit Authorization header (e.g. password reset flows).
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// 401 interceptor: try a one-shot silent /auth/refresh then retry the original request.
// Prevents "Not authenticated" toasts when the 8h access token expires while the user
// is mid-session (especially on iOS Safari where multipart uploads were failing).
let refreshPromise = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    if (status !== 401 || !original || original._retried) throw error;
    const url = original.url || "";
    if (url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/logout")) throw error;
    original._retried = true;
    try {
      if (!refreshPromise) {
        refreshPromise = api.post("/auth/refresh").then((r) => {
          if (r.data?.access_token) setAccessToken(r.data.access_token);
          return r;
        }).finally(() => { refreshPromise = null; });
      }
      await refreshPromise;
      return api(original);
    } catch {
      throw error;
    }
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
}

export function fmtDateTime(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
}

export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export function daysBetween(a, b) {
  const d1 = new Date(a); const d2 = new Date(b);
  return Math.max(0, Math.round((d2 - d1) / 86400000) + 1);
}

export const ROLE_LABELS = {
  super_admin: "System Admin",
  owner_director: "Owner / Director",
  skipper: "Skipper",
  accountant: "Accountant",
  operations_admin: "Operations Admin",
  maintenance_user: "Maintenance",
};

export const RESERVATION_TYPE_LABELS = {
  private: "Private",
  charter_skippered: "Charter (Skippered)",
  excursion: "Excursion",
  maintenance_block: "Maintenance Block",
};

export const STATUS_BADGE = {
  approved: "badge-approved",
  pending_approval: "badge-pending",
  rejected: "badge-rejected",
  cancelled: "badge-neutral",
  draft: "badge-neutral",
};
